import React from "react";

import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Input,
  InputGroupAddon,
  InputGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import Pagination from "../components/common/pagination";
import StoresTable from "../components/StoresTable";
import { getStoreByLocation, getStores } from "../services/storeService";
import { Link } from "react-router-dom";
import DisplayWhen from "../components/common/base/DisplayWhen";
import userHasRole from "../components/shared/utils/authUtils";
import { editorRole, qualityRole } from "../components/shared/constants";
import * as Sentry from "@sentry/react";

const ACTIVE_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "forced_active", label: "Forced Active" },
  { value: "unknown", label: "Unknown" },
];

const HOST_LOCATION_OPTIONS = [
  { value: "", label: "All hosts" },
  { value: "true", label: "Local" },
  { value: "false", label: "Cloud" },
];

class StoresView extends React.Component {
  state = {
    stores: [],
    currentPage: 1,
    count: 0,
    pageSize: 1,
    highLight: false,
    query: "",
    activeFilter: "",
    hostFilter: "",
  };

  timer = null;

  async componentDidMount() {
    const { location } = this.props;
    const params = new URLSearchParams(location?.search);
    const search = params.get("search");
    if (search) {
      this.setState({ query: search }, () => this.fetchStores());
    } else {
      await this.fetchStores();
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    const { query, currentPage, activeFilter, hostFilter } = this.state;
    if (prevState.query !== query) {
      if (query === "" || query.length > 2) this.timer = setTimeout(() => this.fetchStores(), 500);
    } else if (
      prevState.currentPage !== currentPage ||
      prevState.activeFilter !== activeFilter ||
      prevState.hostFilter !== hostFilter
    ) {
      await this.fetchStores();
    }
  }

  componentWillUnmount() {
    if (this.timer) clearTimeout(this.timer);
  }

  fetchStores = async () => {
    const { currentPage, query, activeFilter, hostFilter } = this.state;
    try {
      const { data: pagedStores } = await getStores(currentPage, query, activeFilter, hostFilter);
      const stores = pagedStores.results;
      const { count } = pagedStores;

      let pageSize = 1;
      if (pagedStores.next) {
        pageSize = stores.length;
      } else {
        pageSize = Math.ceil(count / currentPage);
      }

      this.setState({ stores, count, pageSize });
    } catch (ex) {
      console.log(ex);
      toast.error("Failed to connect, please log in");
    }
  };

  handleFindStore = async () => {
    const { query, stores } = this.state;
    const location = query;

    try {
      const { data: store } = await getStoreByLocation(location);
      const originalStores = stores;
      let filteredStores = originalStores.filter((originalStore) => originalStore.id !== store.id);
      filteredStores = [store, ...filteredStores];
      const highLight = true;
      this.setState({ stores: filteredStores, highLight });
    } catch (ex) {
      if (ex.response && ex.response.status === 404) {
        if (ex.response.data && ex.response.data.detail) {
          toast.error(ex.response.data.detail);
        } else {
          toast.error("Store not found");
        }
      } else if (ex.response && ex.response.status === 400) {
        Object.keys(ex.response.data).forEach(function (key) {
          const message = `${key}: ${ex.response.data[key][0]}`;
          toast.error(message);
        });
      } else if (ex.response && ex.response.status === 401) {
        toast.error(ex.response.data.error);
      } else {
        toast.error("Unhandled error");
      }
    }
  };

  handleChange = (value) => {
    clearTimeout(this.timer);
    this.setState({ query: value, currentPage: 1 });
  };

  handleActiveFilterChange = (e) => {
    clearTimeout(this.timer);
    this.setState({ activeFilter: e.target.value, currentPage: 1 });
  };

  handleHostFilterChange = (e) => {
    clearTimeout(this.timer);
    this.setState({ hostFilter: e.target.value, currentPage: 1 });
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  render() {
    const { count, pageSize, currentPage, stores, highLight, query, activeFilter, hostFilter } = this.state;
    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Stores</CardTitle>
                <Row>
                  <Col md="4">
                    <InputGroup>
                      <Input
                        name="find_store"
                        id="find_store"
                        placeholder="Store location"
                        value={query}
                        onChange={(e) => this.handleChange(e.currentTarget.value)}
                      />
                      <InputGroupAddon addonType="append">
                        <Button className="input-group-button" onClick={this.handleFindStore}>
                          Find store
                        </Button>
                      </InputGroupAddon>
                    </InputGroup>
                  </Col>

                  <Col md="2">
                    <Input
                      type="select"
                      bsSize="sm"
                      name="active_filter"
                      id="active_filter"
                      value={activeFilter}
                      onChange={this.handleActiveFilterChange}
                    >
                      {ACTIVE_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md="2">
                    <Input
                      type="select"
                      bsSize="sm"
                      name="host_filter"
                      id="host_filter"
                      value={hostFilter}
                      onChange={this.handleHostFilterChange}
                    >
                      {HOST_LOCATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <DisplayWhen condition={userHasRole(editorRole) && !userHasRole(qualityRole)}>
                    <Col md="2">
                      <Link to="/stores/new">
                        <Button>Add new store</Button>
                      </Link>
                    </Col>
                  </DisplayWhen>
                </Row>
              </CardHeader>
              <CardBody>
                <StoresTable stores={stores} highLight={highLight} />
                <Pagination
                  itemsCount={count}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={this.handlePageChange}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default Sentry.withProfiler(StoresView);
